import React, { useEffect, useRef, useState } from 'react';
import { Check, Plus, ShieldAlert, Trash2, X } from 'lucide-react';
import { MentionInput, structureMentionItems } from './structure/focus';
import { ReservedDomainPicker } from './structure/ReservedDomains';

// Fiche en super édition (administrateurs) : tout se modifie directement, sans tension.

let rowSeq = 0;
const rows = (values) => (values || []).map((value) => ({ id: (rowSeq += 1), value }));

const ListEditor = ({ items, onChange, placeholder, addLabel, mentionItems, getMentionStyle }) => {
  const lastAdded = useRef(null);
  const update = (id, value) => onChange(items.map((row) => (row.id === id ? { ...row, value } : row)));
  const remove = (id) => onChange(items.filter((row) => row.id !== id));
  const add = () => {
    const row = { id: (rowSeq += 1), value: '' };
    lastAdded.current = row.id;
    onChange([...items, row]);
  };
  return (
    <div className="space-y-1.5">
      {items.map((row) => (
        <div key={row.id} className="flex items-center gap-2">
          {mentionItems ? (
            <MentionInput
              value={row.value}
              onChange={(value) => update(row.id, value)}
              mentionItems={mentionItems}
              getMentionStyle={getMentionStyle}
              placeholder={placeholder}
            />
          ) : (
            <input
              value={row.value}
              onChange={(e) => update(row.id, e.target.value)}
              placeholder={placeholder}
              autoFocus={lastAdded.current === row.id}
              className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:border-indigo-300 focus:outline-none"
            />
          )}
          <button type="button" onClick={() => remove(row.id)} className="p-1 text-slate-400 hover:text-rose-500" title="Retirer">
            <X size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-indigo-300 hover:text-indigo-700"
      >
        <Plus size={13} />
        {addLabel}
      </button>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
    {hint && <p className="text-xs text-slate-400 -mt-1">{hint}</p>}
    {children}
  </div>
);

const ownerList = (circle) => {
  const ids = [];
  [circle?.owner_id, ...(circle?.owner_ids || [])].forEach((value) => {
    const id = Number(value);
    if (value !== null && value !== undefined && value !== '' && !Number.isNaN(id) && !ids.includes(id)) ids.push(id);
  });
  return ids;
};

const CircleForm = ({
  circle,
  parentCircle,
  activeEmployees = [],
  findEmployee,
  mentionItems = [],
  getMentionStyle,
  onSave,
  onDelete,
  onCancel,
  parentId,
  isRole,
}) => {
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [owners, setOwners] = useState([]);
  const [reserved, setReserved] = useState([]);
  const [domains, setDomains] = useState([]);
  const [accountabilities, setAccountabilities] = useState([]);
  const [strategy, setStrategy] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(circle?.name || '');
    setPurpose(circle?.purpose || '');
    setOwners(ownerList(circle));
    setReserved(circle?.reserved_domains || []);
    setDomains(rows(circle?.domains));
    setAccountabilities(rows(circle?.accountabilities));
    setStrategy(rows((circle?.strategy || []).map((item) => item.content)));
  }, [circle]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clean = (list) => list.map((row) => (row.value || '').trim()).filter(Boolean);
    const dates = new Map((circle?.strategy || []).map((item) => [item.content, item.updated_at]));
    const ownerIds = isRole ? owners : owners.slice(0, 1);
    setSaving(true);
    await onSave({
      name: name.trim() || 'Sans nom',
      purpose: purpose.trim(),
      owner_id: ownerIds[0] ?? null,
      owner_ids: ownerIds,
      reserved_domains: reserved,
      domains: clean(domains),
      accountabilities: clean(accountabilities),
      strategy: clean(strategy).map((content) => ({ content, updated_at: dates.get(content) || new Date().toISOString() })),
      parent_id: circle ? circle.parent_id ?? null : parentId ?? null,
    });
    setSaving(false);
  };

  const kind = isRole ? 'rôle' : 'cercle';
  const title = circle ? `Modifier ${circle.name}` : `Nouveau ${kind}${parentCircle ? ` dans ${parentCircle.name}` : ''}`;

  return (
    <div className="h-full overflow-y-auto rounded-2xl border border-amber-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-amber-100 bg-amber-50 px-5 py-3">
        <div>
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-700">
            <ShieldAlert size={12} />
            Super édition
          </p>
          <h2 className="text-lg font-bold text-slate-800">{title}</h2>
        </div>
        {circle && (
          <button
            type="button"
            onClick={() => onDelete(circle.id)}
            className="flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"
          >
            <Trash2 size={13} />
            Supprimer
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 p-5">
        <Field label="Nom">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus={!circle}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-base font-semibold text-slate-800 focus:border-indigo-300 focus:outline-none"
            placeholder={isRole ? 'Ex. Pilote projet' : 'Ex. Production'}
          />
        </Field>

        <Field label="Raison d'être">
          <textarea
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-indigo-300 focus:outline-none resize-none"
            placeholder={`Pourquoi ce ${kind} existe-t-il ?`}
          />
        </Field>

        <Field label={isRole ? 'Titulaires' : 'Leader'}>
          {isRole ? (
            <div className="space-y-1.5">
              <div className="flex flex-wrap gap-1.5">
                {owners.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-sm text-slate-700">
                    {findEmployee?.(id)?.name || 'Personne inconnue'}
                    <button type="button" onClick={() => setOwners((prev) => prev.filter((o) => o !== id))} className="text-slate-400 hover:text-rose-500">
                      <X size={13} />
                    </button>
                  </span>
                ))}
                {owners.length === 0 && <span className="text-xs text-slate-400">Rôle vacant.</span>}
              </div>
              <select
                value=""
                onChange={(e) => {
                  const id = Number(e.target.value);
                  if (id && !owners.includes(id)) setOwners((prev) => [...prev, id]);
                }}
                className="w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="">Ajouter une personne…</option>
                {activeEmployees.filter((emp) => !owners.includes(Number(emp.id))).map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>
          ) : (
            <select
              value={owners[0] ?? ''}
              onChange={(e) => setOwners(e.target.value ? [Number(e.target.value)] : [])}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none"
            >
              <option value="">Pas de leader</option>
              {activeEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Redevabilités" hint="Ce que le rôle fait pour les autres. « @ » cite un cercle, un rôle ou une règle.">
          <ListEditor
            items={accountabilities}
            onChange={setAccountabilities}
            placeholder="Ex. Tenir le planning du projet à jour"
            addLabel="Ajouter une redevabilité"
            mentionItems={structureMentionItems(mentionItems)}
            getMentionStyle={getMentionStyle}
          />
        </Field>

        <Field
          label="Domaines"
          hint="Ce que ce rôle contrôle seul. Certains ont un effet dans Deepflow (🔑) : ils réservent un droit aux titulaires, sur les projets de leur focus."
        >
          <div className="space-y-2">
            <ReservedDomainPicker value={reserved} onChange={setReserved} />
            <ListEditor items={domains} onChange={setDomains} placeholder="Ex. Le discours client" addLabel="Ajouter un domaine"
              mentionItems={structureMentionItems(mentionItems)} getMentionStyle={getMentionStyle} />
          </div>
        </Field>

        {!isRole && (
          <Field label="Stratégie">
            <ListEditor items={strategy} onChange={setStrategy} placeholder="Ex. Priorité aux projets récurrents" addLabel="Ajouter une orientation" />
          </Field>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
          <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            <Check size={16} />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CircleForm;
